import React, { FC, useEffect } from 'react';
import Grid from '@mui/material/Grid';
import FormControl from '@mui/material/FormControl';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Checkbox from '@mui/material/Checkbox';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import { useTranslation } from 'react-i18next';
import { CONSTANTS } from 'config/constants';
import { PropertyBox, PropertyLabel } from 'components/property-settings/property-shared-styles';
import { Box } from '@mui/system';

const COLOR_FREE_TEXT_MAX_LENGTH = 50;

type ShadeProps = {
    handleSettings?: (propertyName: string, settingValue: string | boolean) => void;
    shade: string;
    color: string;
    colorSampling: boolean;
    isRequired?: boolean;
    onChange?: (properties: { shade: string; color: string; colorSampling: boolean }) => void;
};

const Shade: FC<ShadeProps> = ({
    handleSettings,
    shade: partShade,
    color: partColor,
    colorSampling: partColorSampling,
    isRequired,
    onChange,
}: ShadeProps) => {
    const { t: translate } = useTranslation(['construction-props']);
    const [shade, setShade] = React.useState(CONSTANTS.SHADES[1].name);
    const [colors, setColors] = React.useState<string[] | null>(CONSTANTS.SHADES[1].colors);
    const [color, setColor] = React.useState('');
    const [colorFreeText, setColorFreeText] = React.useState('');
    const [colorSampling, setColorSampling] = React.useState(false);

    const findShadeColors = (shadeName: string): string[] | null => {
        const shadeOption = CONSTANTS.SHADES.find((shadeItem) => shadeItem.name === shadeName);
        return shadeOption?.colors ?? null;
    };

    useEffect(() => {
        const shadeName = partShade || shade;
        const shadeColors = findShadeColors(shadeName);
        // A color stored together with a shade came from the predefined dropdown lists;
        // a color stored without a shade was entered as free text.
        const isColorFromList = Boolean(partShade && partColor && shadeColors?.includes(partColor));
        setShade(shadeName);
        if (shadeColors) {
            setColors(shadeColors);
        }
        setColor(isColorFromList ? partColor : '');
        setColorFreeText(partColor && !isColorFromList ? partColor : '');
        setColorSampling(Boolean(partColorSampling || false));
    }, [partShade, partColor, partColorSampling]);

    const handleShadeChange = (event: SelectChangeEvent<string>) => {
        const shadeName = event.target.value;
        if (CONSTANTS.SHADES.length) {
            const tempObj = CONSTANTS.SHADES.find((shade) => shadeName === shade.name);
            if (tempObj?.colors) {
                setColors(tempObj.colors);
            }
        }
        setColor('');
        setColorFreeText('');
        setColorSampling(false);
        setShade(shadeName);
        if (handleSettings) {
            handleSettings('shade', shadeName);
            handleSettings('color', '');
            handleSettings('colorSampling', false);
        }
        if (onChange) {
            onChange({ shade: shadeName, color: '', colorSampling: false });
        }
    };

    const handleColorChange = (event: SelectChangeEvent<string>) => {
        setColor(event.target.value);
        setColorFreeText('');
        setColorSampling(false);
        if (handleSettings) {
            handleSettings('color', event.target.value);
            handleSettings('shade', shade);
            handleSettings('colorSampling', false);
        }
        if (onChange) {
            onChange({ shade, color: event.target.value, colorSampling: false });
        }
    };

    const handleColorFreeTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const freeTextValue = event.target.value.slice(0, COLOR_FREE_TEXT_MAX_LENGTH);
        // Free text replaces the whole shade/color selection, so no shade is saved with it;
        // clearing the text restores the current shade selection.
        const shadeToSave = freeTextValue ? '' : shade;
        setColorFreeText(freeTextValue);
        setColor('');
        setColorSampling(false);
        if (handleSettings) {
            handleSettings('shade', shadeToSave);
            handleSettings('color', freeTextValue);
            handleSettings('colorSampling', false);
        }
        if (onChange) {
            onChange({ shade: shadeToSave, color: freeTextValue, colorSampling: false });
        }
    };

    const handleColorSampling = (_event: React.SyntheticEvent, checked: boolean) => {
        setColor('');
        setColorFreeText('');
        setColorSampling(checked);
        if (handleSettings) {
            handleSettings('shade', shade);
            handleSettings('color', '');
            handleSettings('colorSampling', checked);
        }
        if (onChange) {
            onChange({ shade, color: '', colorSampling: checked });
        }
    };

    const isFreeTextEntered = colorFreeText !== '';
    const isColorSelectedFromDropdown = color !== '';
    const isFreeTextLimitReached = colorFreeText.length >= COLOR_FREE_TEXT_MAX_LENGTH;

    return (
        <PropertyBox>
            <Box display="flex">
                <PropertyLabel variant="h5">{translate('SHADE')}</PropertyLabel>
                {isRequired && (
                    <Typography
                        component="span"
                        color="error"
                        fontSize={18}
                        sx={{ marginLeft: '2px', marginTop: '-5px' }}
                    >
                        *
                    </Typography>
                )}
            </Box>
            <Grid container spacing={1}>
                <Grid item xs={7}>
                    <FormControl fullWidth>
                        <InputLabel id="shade-label" sx={{ fontSize: '12px' }}>
                            {translate('SHADE')}
                        </InputLabel>
                        <Select
                            labelId="shade-label"
                            id="shade-select"
                            value={isFreeTextEntered ? '' : shade}
                            onChange={handleShadeChange}
                            label={translate('SHADE')}
                            disabled={isFreeTextEntered}
                            sx={{
                                '& .MuiSelect-select': {
                                    minHeight: 10,
                                    fontSize: '12px',
                                    paddingTop: '6px',
                                    paddingBottom: '6px',
                                },
                                '& .MuiInputBase-root': {
                                    height: 10,
                                    fontSize: '12px',
                                },
                            }}
                        >
                            {CONSTANTS.SHADES.map((shade, index) => {
                                return (
                                    <MenuItem key={index} value={shade.name}>
                                        {shade.name}
                                    </MenuItem>
                                );
                            })}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={5}>
                    <FormControl fullWidth>
                        <InputLabel id="color-label" sx={{ fontSize: '12px' }}>
                            {translate('COLOR')}
                        </InputLabel>
                        <Select
                            labelId="color-label"
                            id="color-select"
                            value={color}
                            onChange={handleColorChange}
                            label={translate('COLOR')}
                            disabled={isFreeTextEntered}
                            sx={{
                                '& .MuiSelect-select': {
                                    minHeight: 10,
                                    fontSize: '12px',
                                    paddingTop: '6px',
                                    paddingBottom: '6px',
                                },
                                '& .MuiInputBase-root': {
                                    height: 10,
                                    fontSize: '12px',
                                },
                            }}
                        >
                            <MenuItem value="">
                                <em>{translate('NONE')}</em>
                            </MenuItem>
                            {colors &&
                                colors.map((color, index) => {
                                    return (
                                        <MenuItem key={index} value={color}>
                                            {color}
                                        </MenuItem>
                                    );
                                })}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={12}>
                    <TextField
                        fullWidth
                        id="color-free-text"
                        value={colorFreeText}
                        label={translate('SHADE_COLOR_FREE_TEXT')}
                        onChange={handleColorFreeTextChange}
                        disabled={isColorSelectedFromDropdown}
                        inputProps={{ maxLength: COLOR_FREE_TEXT_MAX_LENGTH }}
                        helperText={
                            isFreeTextLimitReached
                                ? translate('FREE_TEXT_MAX_LENGTH_REACHED', {
                                      maxLength: COLOR_FREE_TEXT_MAX_LENGTH,
                                  })
                                : undefined
                        }
                        sx={{
                            '& .MuiInputBase-root': {
                                height: 32,
                                fontSize: '12px',
                            },
                            '& .MuiInputLabel-root': {
                                fontSize: '12px',
                            },
                            '& .MuiFormHelperText-root': {
                                fontSize: '11px',
                                marginLeft: 0,
                            },
                        }}
                    />
                </Grid>
            </Grid>

            <FormControl component="fieldset">
                <FormGroup row>
                    <FormControlLabel
                        control={<Checkbox color="secondary" checked={colorSampling} onChange={handleColorSampling} />}
                        label={translate('COLOR_SAMPLING_AT_LAB')}
                        labelPlacement="end"
                    />
                </FormGroup>
            </FormControl>
        </PropertyBox>
    );
};

export default Shade;
